// src/pages/OrdersPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import orderService from '../services/orderService';
import OrderCard from '../components/order/OrderCard';
import Loading from '../components/common/Loading';
import { getErrorMessage } from '../utils/helpers';

/**
 * Orders Page Component
 * 
 * Features:
 * - Display order history
 * - Show order status
 * - Cancel orders
 * - Link to order details
 */
const OrdersPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    fetchOrders();
  }, [isAuthenticated, navigate]);
  
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const data = await orderService.getUserOrders();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelOrder = async (orderId) => {
    if (!window.confirm(
      'Cancel this order?\n\nIf you paid online, your refund will be initiated automatically ' +
      'and should reach your account in 5–7 business days.'
    )) {
      return;
    }

    try {
      const order = orders.find((o) => o.id === orderId);
      await orderService.cancelOrder(orderId);
      const wasPaidOnline = order?.paymentMethod === 'RAZORPAY' && order?.paymentCompleted;
      alert(wasPaidOnline
        ? 'Order cancelled. Your refund has been initiated and will reach you in 5–7 business days.'
        : 'Order cancelled successfully.');
      fetchOrders(); // Refresh list
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };
  
  if (loading) {
    return <Loading fullScreen message="Loading your orders..." />;
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Orders</h1>
          <p className="text-gray-600 mt-2">
            View and manage your orders
          </p>
        </div>
        
        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg
              className="mx-auto h-24 w-24 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No orders yet
            </h3>
            <p className="mt-2 text-gray-600">
              Start shopping to place your first order
            </p>
            <button
              onClick={() => navigate('/products')}
              className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Products
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                onCancelOrder={handleCancelOrder}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersPage;